/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { VisionApplication } from '../../application';
import { FormElements } from '../../components/form';
import { Measurement } from '../../components/measurement';
import { TimeSelect } from '../../components/timeselect';
import { VisionLayout } from '../../layout';
import { TrackLine } from '../../map/polyline';
import { AssetService } from '../../service/asset';
import { EventDataResultDTO } from '../../service/event_service';
import { PaginatedFetcher } from '../../service/paginatedFetcher';
import { GradingDTO } from '../grading_tab/grading_service';

import { PlowingMap } from './map';
import { PlowingDTO, PlowingService } from './plowing_service';

/** The main Grading Tab for the application.
 *
 */
export class VisionPlowingTab {
    // UI Elements
    layout!: dhtmlXLayoutObject;
    assetGrid!: dhtmlXGridObject;
    paramForm!: dhtmlXForm;
    toolbar!: dhtmlXToolbarObject;
    tabbar!: dhtmlXTabBar;

    app: VisionApplication;
    service: PlowingService;
    map!: PlowingMap;

    count!: number; // Number of assets
    polylineData!: Map<string, TrackLine>; // Asset data map keyed on asset id

    highlightedTrack: TrackLine | undefined; // Current asset selected.
    allSelected: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    statusbar!: any;
    cancelRequest: boolean;
    fetcher: PaginatedFetcher<PlowingDTO> | undefined;
    heatData: { location: google.maps.LatLng; weight: number }[] = [];

    /** Constructor
     *  TODO: Remove paramter because layout can be found from the Singleton.
     * @param vision The main layout object.
     */
    constructor(vision: VisionLayout) {
        this.app = VisionApplication.getInstance();
        this.service = new PlowingService();
        this.polylineData = new Map<string, TrackLine>();
        this.highlightedTrack = undefined;
        this.allSelected = true;
        this.cancelRequest = false;
        this.fetcher = undefined;

        const config: IdhtmlXLayoutConfig = {
            parent: document.body,
            pattern: '3J',
        };

        this.layout = vision.tabbar.cells('PLOWING').attachLayout(config);
    }

    public init() {
        this.buildUI();
        this.populateGrid();
        this.buildEvents();
    }

    /** Builds all the GUI elements required.
     *
     */
    private buildUI(): void {
        this.layout.cells('c').setText('Plows');
        this.assetGrid = this.layout.cells('c').attachGrid();
        this.assetGrid.setImagesPath('css/imgs/');
        this.layout.cells('a').hideHeader();

        this.assetGrid.setHeader('Show,Name,Total Miles,Plowed Miles,%', '', ['text-align:left;', 'text-align:center;', 'text-align:center;', 'text-align:center;']);
        this.assetGrid.setColSorting('int,int,int,int,int');
        this.assetGrid.setColTypes('ch,ro,ro,ro,ro');
        this.assetGrid.setColAlign('left,center,center,center,center');
        this.assetGrid.init();

        this.toolbar = this.layout.cells('c').attachToolbar();
        //this.toolbar.addButton('new', 0, "Refresh", "css/imgs/new.gif", "");
        this.toolbar.addButtonTwoState('tracks', 1, 'Hide Tracks', '', '');

        this.tabbar = this.layout.cells('b').attachTabbar();

        this.tabbar.addTab('map', 'MAP', 100, 1, true, false);
        //this.tabbar.addTab("dashboard", "Dashboard", 100, 2, false, false);

        this.map = new PlowingMap(this);

        const formData = JSON.parse(JSON.stringify(TimeSelect.SHORT_FORM));
        formData.push({
            type: 'block',
            list: [FormElements.button('search', 'Search', 'search'), { type: 'newcolumn' }, FormElements.button('cancel', 'Cancel', 'cancel')],
        });

        formData.push({
            type: 'block',
            list: [FormElements.button('pdf', 'Report (date)', 'report_by_date'), { type: 'newcolumn' }, FormElements.button('pdf', 'Report (asset)', 'report_by_asset')],
        });

        this.paramForm = this.layout.cells('a').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';
        this.paramForm.getInput(TimeSelect.END).autocomplete = 'off';
        this.paramForm.hideItem('cancel');

        this.layout.cells('a').fixSize(false, true);
        this.layout.cells('a').setHeight(300);
        this.layout.cells('a').setWidth(400);
        this.statusbar = this.layout.cells('a').attachStatusBar();
    }

    /** Build all the GUI event handlers
     *
     */
    private buildEvents(): void {
        // Tool bar handlers
        this.toolbar.attachEvent('onStateChange', (itemId, state) => {
            this.toolbarButtonChange(itemId, state);
        });

        // Asset Grid Handlers
        this.assetGrid.attachEvent('onRowSelect', (id) => {
            this.selectPlow(id);
        });
        this.assetGrid.attachEvent('onHeaderClick', (id, _obj) => {
            this.headerClicked(id);
        });
        this.assetGrid.attachEvent('onCheck', (rid, _cid, state) => {
            this.rowChecked(rid, state);
        });

        // Form Events
        this.paramForm.attachEvent('onChange', (name, value, state) => {
            this.formChange(name, value, state);
        });

        this.paramForm.attachEvent('onButtonClick', (id) => {
            switch (id) {
                case 'search': {
                    this.paramForm.showItem('cancel');
                    this.getData();
                    this.cancelRequest = false;
                    break;
                }
                case 'report_by_date':
                    this.getReportByDate();
                    break;
                case 'report_by_asset':
                    this.getReportByAsset();
                    break;

                case 'cancel': {
                    if (this.fetcher != undefined) {
                        this.fetcher.cancel();
                    }
                }
            }
        });
    }

    public formChange(name: string, value: string | number, _state: boolean) {
        if (name == TimeSelect.SELECT) {
            this.populateTime(value);
        }
    }
    private populateTime(period: string | number): void {
        const select = new TimeSelect(this.paramForm);
        select.selectRange(period as string);
    }

    /** User has selected a plow from the grid list.
     * This will move the map to show the track for that plow.
     *
     * @param id
     */
    private selectPlow(id: string): void {
        const track = this.polylineData.get(id);
        if (track) {
            //this.highlightedTrack?.setColour("#FF00FF");
            this.map.map.map.fitBounds(track.bounds);
            //track.setColour("#00fff2");
            this.highlightedTrack = track;
        }
    }

    /** When the users clicks one of the two state buttons in the toolbar
     *
     * @param itemId The name of the button:
     *  * tracks
     * @param newstate The new state of the button
     */
    private toolbarButtonChange(itemId: string, newstate: boolean): void {
        switch (itemId) {
            case 'tracks': {
                this.polylineData.forEach((data) => {
                    data.setVisisble(!newstate);
                });
                break;
            }
        }
    }

    /** Request to server to get PDF grader report
     *
     */
    private getReportByDate(): void {
        const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();
        if (!FormElements.verifyQueryTimes(startTime, endTime)) {
            return;
        }

        this.service.downloadPDF('/report/plow/summary/date?start=' + startTime + '&end=' + endTime, 'Plow-Report.pdf');
    }

    private getReportByAsset(): void {
        const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();

        if (!FormElements.verifyQueryTimes(startTime, endTime)) {
            return;
        }

        this.service.downloadPDF('/report/plow/summary/asset?start=' + startTime + '&end=' + endTime, 'Plow-Report.pdf');
    }

    /** Request to server to get the Grader data.
     *
     */
    private getData(): void {
        this.layout.cells('a').progressOn();
        const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();

        if (!FormElements.verifyQueryTimes(startTime, endTime)) {
            return;
        }

        const activeTab = this.tabbar.getActiveTab();

        if (activeTab == 'map') {
            this.heatData = [];
            const url = '/plow/trackdata?start=' + startTime + '&end=' + endTime;
            const fetcher = new PaginatedFetcher<PlowingDTO>(url);
            this.fetcher = fetcher;

            fetcher.fetchAllPagesComplete(
                (pageData, page, totalPages) => {
                    this.statusbar.setText('Loading Track Data ' + (page + 1) + '/' + totalPages);
                    this.loadData(pageData);
                },
                () => {
                    this.displayHeatmap();
                }
            );

            this.getTableData(startTime, endTime);
        }
    }

    private getTableData(startUTC: number, endUTC: number) {
        this.service.getDailyData(startUTC, endUTC).then((data) => {
            const map = new Map<string, EventDataResultDTO>();
            if (data.parsedBody) {
                data.parsedBody.forEach((item) => {
                    let result;

                    if (map.has(item.id)) {
                        result = map.get(item.id);
                    } else {
                        result = {} as EventDataResultDTO;
                        result.eventKm = 0;
                        result.totalKm = 0;
                    }
                    if (result) {
                        result.eventKm += item.eventKm;
                        result.totalKm += item.totalKm;
                        map.set(item.id, result);
                    }
                });

                map.forEach((data, key) => {
                    const totalMiles = Math.round(Measurement.km2miles(data.totalKm));
                    const totalEventMiles = Math.round(Measurement.km2miles(data.eventKm));

                    this.assetGrid.cells(key, 2).setValue(String(totalMiles));
                    this.assetGrid.cells(key, 3).setValue(String(totalEventMiles));
                    this.assetGrid.cells(key, 4).setValue(String(Math.round((data.eventKm / data.totalKm) * 100)) + ' %');
                });
            }
        });
    }

    /** Get the status of the motorgrader input.
     *
     * @param assetid The id of the asset
     * @param dto The DTO data point from the asset.
     * @returns
     */
    getPlowInput(assetid: string, dto: GradingDTO): boolean {
        const asset = this.app.assetService.assetList.get(assetid);
        if (asset) {
            const input = AssetService.findActivity('PLOW', asset.config);
            if (input != undefined) {
                switch (input) {
                    case 1:
                        return dto.input1 == true;
                    case 2:
                        return dto.input2 == true;
                    case 3:
                        return dto.input3 == true;
                    case 4:
                        return dto.input4 == true;
                    default:
                        return false;
                }
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    private displayHeatmap(): void {
        // Random colour generator.
        const gradientColors = this.generateUniqueColors(this.count);
        let c = 0;

        // Sets the colour of the track line and in the asset grid.

        this.polylineData.forEach((data, key) => {
            const colour = gradientColors[c];
            c = c + 1;
            this.assetGrid.cells(key, 0).setBgColor(colour);
            data.setColour(colour);
        });

        this.map.setData(this.heatData);
        this.layout.cells('a').progressOff();
    }

    /** Loads the data that was retrived from the server.
     *
     * @param parsedBody
     */
    private loadData(parsedBody: GradingDTO[] | undefined): void {
        //var heatData: { location: google.maps.LatLng; weight: number; }[] = []
        const bounds = new google.maps.LatLngBounds();

        // Random colour generator.
        //var gradientColors = this.generateUniqueColors(this.count);
        //var c = 0;

        // For all the data in the results
        //  * get the status for the input and if active push onto the heat map
        parsedBody?.forEach((data) => {
            const ext = this.loadTrackData(data);
            if (ext != null) {
                bounds.union(ext);

                const activityOn = this.getPlowInput(data.assetId, data);
                if (activityOn == true) {
                    this.heatData.push({
                        location: new google.maps.LatLng(data.lat, data.lon),
                        weight: 10,
                    });
                }
            }
        });

        // Sets the colour of the track line and in the asset grid.
        //var c = 0;
        //this.polylineData.forEach ( (data, key) =>{
        //    var colour = gradientColors[c];
        //    c = c+1;
        //    this.assetGrid.cells(key, 0).setBgColor(colour);
        //    data.setColour(colour);
        //});

        //this.map.setData(heatData);
        this.map.map.map.fitBounds(bounds);
        this.layout.cells('a').progressOff();
    }

    /** Load the track line data.
     * Looks into the cache of tracklines and adds the data point to it.
     *
     * @param data The DTO object from the server with the track lat/lon
     */
    private loadTrackData(data: GradingDTO): google.maps.LatLngBounds | null {
        if (data.lat == 0 && data.lon == 0) {
            return null;
        }

        let trackLine = this.polylineData.get(data.assetId);
        if (trackLine == null) {
            trackLine = new TrackLine();
        }
        trackLine.showMarkers(false);
        trackLine.addPoint(data.lat, data.lon, '#00FF00', 0);
        trackLine.setMap(this.map.map);
        this.polylineData.set(data.assetId, trackLine);

        return trackLine.bounds;
    }

    /** Populate the grid of assets.
     * Uses the chache list of assets to build the list.
     *
     */
    private populateGrid(): void {
        this.assetGrid.clearAll(false);
        this.count = 0;

        this.app.assetService.assetList.forEach((asset) => {
            if (asset.assetType == 'SALTER_SANDER') {
                this.assetGrid.addRow(asset.id.id, [1, asset.id.name], 0, 0, '', false);
                this.count = this.count + 1;
            }
        });
    }

    /** The header in the asset list was clicked.
     *  This is used to turn on/off all the check boxes.
     *
     * @param id The column index that was clicked ( 0 = the checkbox )
     */
    private headerClicked(id: number) {
        if (id == 0) {
            this.allSelected = !this.allSelected;
            if (this.allSelected == false) {
                this.assetGrid.uncheckAll();
                this.polylineData.forEach((item) => {
                    item.setVisisble(false);
                });
            } else {
                this.assetGrid.checkAll(true);
                this.polylineData.forEach((item) => {
                    item.setVisisble(true);
                });
            }
        }
    }

    /** Called when the user checks a row in the asset list.
     *  Will turn the track on/off
     *
     * @param id The id of the row ( ie asset id )
     * @param state The state of the checkbox
     */
    private rowChecked(id: string, state: boolean): void {
        const grader = this.polylineData.get(id);
        grader?.polyline.setVisible(state);
    }

    /** ChatGPT function that will generate a nice set of colours
     *
     * @param count
     * @returns
     */
    private generateUniqueColors(count: number): string[] {
        // Helper function to convert HSV to RGB
        function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
            const c = v * s;
            const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
            const m = v - c;
            let rgb: [number, number, number] = [0, 0, 0];

            if (h < 60) rgb = [c, x, 0];
            else if (h < 120) rgb = [x, c, 0];
            else if (h < 180) rgb = [0, c, x];
            else if (h < 240) rgb = [0, x, c];
            else if (h < 300) rgb = [x, 0, c];
            else rgb = [c, 0, x];

            return [Math.round((rgb[0] + m) * 255), Math.round((rgb[1] + m) * 255), Math.round((rgb[2] + m) * 255)];
        }

        // Helper function to convert RGB to hex
        function rgbToHex(r: number, g: number, b: number): string {
            return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        }

        const colors: string[] = [];
        const hueStep = 360 / count; // Equal spacing in hue
        const saturation = 0.8; // Strong saturation for vibrant colors
        const value = 0.9; // High brightness for visibility

        for (let i = 0; i < count; i++) {
            const hue = i * hueStep;
            const [r, g, b] = hsvToRgb(hue, saturation, value);
            colors.push(rgbToHex(r, g, b));
        }
        return colors;
    }

    public onSelect(): void {
        this.app.setMapWorkspace(this.map.map.map);
    }
}
